import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useEffect, useState } from "react";
import { postRequest } from "../../services/mainApp.service";
import { useSelector } from "react-redux";
import { Loader2, Search, Calendar, MapPin, Building, CreditCard, Truck, User, Phone, FileText, History, Plus, Minus, ChevronLeft, ChevronRight, Tag } from "lucide-react";

export function CustomerOrderHistoryDialog({ open, onOpenChange, initialPhoneNumber }) {
  const userData = useSelector((state) => state.authReducer);
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber || "");
  const [loading, setLoading] = useState(false);
  const [orderHistory, setOrderHistory] = useState([]);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [searched, setSearched] = useState(false);
  const [expandedOrders, setExpandedOrders] = useState(new Set());

  // DataTables pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Fix: Add this useEffect to handle initialPhoneNumber changes
  useEffect(() => {
    if (initialPhoneNumber) {
      setPhoneNumber(initialPhoneNumber);
    }
  }, [initialPhoneNumber]);

  // Fix: This useEffect should trigger fetch when dialog opens with a phone number
  useEffect(() => {
    if (open && phoneNumber) {
      // Only fetch if we haven't searched for this phone number yet
      if (!searched || orderHistory.length === 0) {
        fetchCustomerOrderHistory(phoneNumber);
      }
    }
  }, [open, phoneNumber]);

  // Calculate paginated orders
  useEffect(() => {
    if (orderHistory.length > 0) {
      const totalPages = Math.ceil(orderHistory.length / itemsPerPage);
      setTotalPages(totalPages);
      // Reset to page 1 if current page is beyond total pages
      if (currentPage > totalPages) {
        setCurrentPage(1);
      }
    } else {
      setTotalPages(1);
      setCurrentPage(1);
    }
  }, [orderHistory, itemsPerPage, currentPage]);

  const getPaginatedOrders = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return orderHistory.slice(startIndex, endIndex);
  };

  const fetchCustomerOrderHistory = async (number = phoneNumber) => {
    if (!number) return;

    setLoading(true);
    setSearched(true);
    try {
      const response = await postRequest("CrudCustomer", {
        OperationId: 5,
        CompanyId: userData.CompanyId,
        PhoneNumber: number,
        UserId: userData.UserId,
        UserIP: userData.UserIP
      });

      if (response.data.Response) {
        // console.log("API Response:", response.data);
        const table = response.data.DataSet?.Table || []; // Summary data
        const data = response.data.Data || []; // Items data with DealDetail

        // Group order items by OrderMasterId from Data array
        const ordersWithItems = table.map(order => {
          const items = data.filter(item => item.OrderMasterId === order.OrderMasterId);
          return {
            ...order, // This contains the summary from Table
            items: items, // This contains the items with DealDetail from Data
            hasItems: items.length > 0 // Add flag to check if order has items
          };
        });

        // Extract customer info from the first order
        if (table.length > 0) {
          const firstOrder = table[0];
          setCustomerInfo({
            customerName: firstOrder.CustomerName || "Unknown Customer",
            phoneNumber: firstOrder.PhoneNumber,
            completeAddress: firstOrder.CompleteAddress,
            cityName: firstOrder.CityName,
            areaName: firstOrder.AreaName
          });
        }

        setOrderHistory(ordersWithItems);
        setCurrentPage(1); // Reset to first page when new data loads
      } else {
        setOrderHistory([]);
        setCustomerInfo(null);
      }
    } catch (error) {
      console.error("Error fetching customer order history:", error);
      setOrderHistory([]);
      setCustomerInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => fetchCustomerOrderHistory();
  const handleKeyPress = (e) => e.key === "Enter" && handleSearch();

  const toggleOrderExpansion = (orderId) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  // Pagination handlers
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const resetDialog = () => {
    setPhoneNumber(initialPhoneNumber || "");
    setOrderHistory([]);
    setCustomerInfo(null);
    setSearched(false);
    setExpandedOrders(new Set());
    setLoading(false);
    setCurrentPage(1);
    setItemsPerPage(10);
  };

  const handleOpenChange = (open) => {
    if (!open) resetDialog();
    onOpenChange(open);
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "00:00:00") return "N/A";

    try {
      if (dateString.includes('/')) {
        const [datePart, timePart] = dateString.split(' ');
        const [day, month, year] = datePart.split('/');
        return new Date(`20${year}-${month}-${day}T${timePart}`).toLocaleString();
      } else {
        return new Date(dateString).toLocaleString();
      }
    } catch (error) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "N/A";
    return `Rs. ${parseFloat(amount).toFixed(2)}`;
  };

  const getStatusBadge = (statusId) => {
    const statusMap = {
      1922: { label: "Pending", color: "bg-yellow-100 text-yellow-800" },
      1923: { label: "Completed", color: "bg-green-100 text-green-800" },
      1927: { label: "Cancelled", color: "bg-red-100 text-red-800" }
    };

    const status = statusMap[statusId] || { label: "Unknown", color: "bg-gray-100 text-gray-800" };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${status.color}`}>
        {status.label}
      </span>
    );
  };

  const getPaymentModeIcon = (paymentMode) => {
    if (!paymentMode) return null;

    if (paymentMode.toLowerCase().includes('card')) {
      return <CreditCard className="h-4 w-4" />;
    } else if (paymentMode.toLowerCase().includes('cash')) {
      return <FileText className="h-4 w-4" />;
    }
    return null;
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  // Function to render order items in the requested format
  const renderOrderItems = (items, order) => {
    if (!items || items.length === 0) {
      return (
        <div className="text-center p-4 bg-white rounded border">
          <div className="text-gray-500">No items data available for this order</div>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {items.map((item, index) => (
          <div key={`${item.ProductDetailId}-${item.RandomId}-${index}`} className="bg-white rounded border">
            {/* Main Product */}
            <div className="flex justify-between items-center p-3 border-b">
              <div className="flex-1">
                <div className="font-medium">{item.ProductName}</div>
                <div className="text-sm text-gray-600">
                  {item.SizeName && item.SizeName !== "-" ? `${item.SizeName}` : ""}
                  {item.FlavourName && item.FlavourName !== "-" ? ` • ${item.FlavourName}` : ""}
                </div>
                {item.SpecialInstruction && (
                  <div className="text-xs text-blue-600 mt-1">
                    Special: {item.SpecialInstruction}
                  </div>
                )}
                {item.DealItemId && (
                  <div className="text-xs text-green-600 mt-1">
                    Included in deal
                  </div>
                )}

                {/* Discount Badge for Item */}
                {/* {item.DiscountPercent > 0 && (
                  <div className="flex items-center gap-1 mt-1">
                    <Tag className="h-3 w-3 text-green-600" />
                    <span className="text-xs text-green-600 font-medium">
                      {item.DiscountPercent}% Discount Applied
                    </span>
                  </div>
                )} */}
              </div>
              <div className="text-right">
                {/* Show original price and discounted price if discount exists */}
                {/* {item.DiscountPercent > 0 ? (
                  <>
                    <div className="text-sm text-gray-500 line-through">
                      {formatCurrency(item.PriceWithoutGST)}
                    </div>
                    <div className="font-medium text-green-600">
                      {formatCurrency(item.PriceWithGST)}
                    </div>
                    <div className="text-xs text-green-600">
                      Save: {formatCurrency(item.PriceWithoutGST - item.PriceWithGST)}
                    </div>
                  </>
                ) : (
                )} */}
                <div className="font-medium">{formatCurrency(item.PriceWithGST)}</div>
                <div className="text-sm text-gray-600">Qty: {item.Quantity}</div>
              </div>
            </div>

            {/* Deal Details */}
            {item.DealDetail && item.DealDetail.length > 0 && (
              <div className="bg-gray-50 p-3">
                <div className="text-sm font-medium text-gray-700 mb-2">Includes:</div>
                <div className="space-y-2 ml-4">
                  {item.DealDetail.map((dealItem, dealIndex) => (
                    <div key={dealIndex} className="flex justify-between items-center text-sm">
                      <div className="flex-1">
                        <span className="text-gray-600">{dealItem.ProductName}</span>
                        {dealItem.SizeName && dealItem.SizeName !== "-" && (
                          <span className="text-xs text-gray-500 ml-2">({dealItem.SizeName})</span>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="text-gray-600">
                          {dealItem.Price > 0 ? formatCurrency(dealItem.PriceWithGST) : "Included"}
                        </span>
                        <span className="text-xs text-gray-500 ml-2">Qty: {dealItem.Quantity}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Order Summary - Using data from the Table array (order object) */}
        {order && (
          <div className="mt-4 pt-4 px-3">
            {/* <h4 className="font-medium text-gray-700 mb-3">Order Summary</h4> */}
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              <div className="space-y-2">
                {/* <div className="flex justify-between">
                  <span className="text-sm"><strong>Order ID:</strong></span>
                  <span className="text-sm">{order.OrderMasterId}</span>
                </div> */}
                <div className="flex justify-between">
                  <span className="text-sm"><strong>Subtotal:</strong></span>
                  <span className="text-sm">{formatCurrency(order.Gross)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm"><strong>Tax ({order.GSTPercent}%):</strong></span>
                  <span className="text-sm">{formatCurrency(order.Tax)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm"><strong>Delivery Charges:</strong></span>
                  <span className="text-sm">{formatCurrency(order.DeliveryCharges)}</span>
                </div>
                {/* {order.Discount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-sm"><strong>Order Discount:</strong></span>
                    <span className="text-sm text-green-600 font-medium">
                      - {formatCurrency(order.Discount)}
                    </span>
                  </div>
                )} */}
                {order.VoucherAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-sm"><strong>Voucher Discount:</strong></span>
                    <span className="text-sm text-green-600">
                      - {formatCurrency(order.VoucherAmount)}
                    </span>
                  </div>
                )}
                {order.Tip > 0 && (
                  <div className="flex justify-between">
                    <span className="text-sm"><strong>Tip:</strong></span>
                    <span className="text-sm">{formatCurrency(order.Tip)}</span>
                  </div>
                )}
                {order.Remarks && (
                  <div className="flex justify-between">
                    <span className="text-sm"><strong>Remarks:</strong></span>
                    <span className="text-sm">{order.Remarks}</span>
                  </div>
                )}
              </div>

              <div className="space-y-2">

                {/* Total Discount Display */}
                {(order.Discount > 0 || order.VoucherAmount > 0) && (
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-sm font-medium"><strong>Total Discount:</strong></span>
                    <span className="text-sm font-medium text-red-600">
                      - {formatCurrency((order.Discount || 0) + (order.VoucherAmount || 0))}
                    </span>
                  </div>
                )}

                <div className="flex justify-between border-t pt-2">
                  <span className="text-lg font-bold"><strong>Total Amount:</strong></span>
                  <span className="text-lg font-bold">
                    {formatCurrency(order.NetAmount)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const paginatedOrders = getPaginatedOrders();

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-center flex items-center justify-center gap-2">
            <History className="h-6 w-6" />
            Customer Order History
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Search Section */}
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <Label htmlFor="searchPhone" className="text-lg font-medium">
                Phone Number *
              </Label>
              <Input
                id="searchPhone"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter customer phone number"
                className="mt-1"
                disabled={loading}
              />
            </div>
            <Button
              onClick={handleSearch}
              disabled={!phoneNumber || loading}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              <span className="ml-2">Search</span>
            </Button>
          </div>

          {/* Results Section */}
          {searched && (
            <div className="space-y-4">
              {/* Customer Info Card */}
              {customerInfo && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-2 rounded-full">
                        <User className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{customerInfo.customerName}</h3>
                        <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                          <div className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            <span>{customerInfo.phoneNumber}</span>
                          </div>
                          {customerInfo.completeAddress && (
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              <span>{customerInfo.completeAddress}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Total Orders</div>
                      <div className="text-2xl font-bold text-blue-600">{orderHistory.length}</div>
                    </div>
                  </div>
                </div>
              )}

              {loading ? (
                <div className="flex justify-center items-center h-32">
                  <Loader2 className="h-8 w-8 animate-spin" />
                  <span className="ml-2">Loading order history...</span>
                </div>
              ) : orderHistory.length > 0 ? (
                <div className="space-y-4">
                  {/* Summary Stats - Updated to include discount stats */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-white p-4 rounded-lg border shadow-sm">
                      <div className="text-sm text-gray-600">Total Spent</div>
                      <div className="text-xl font-bold">
                        {formatCurrency(orderHistory.reduce((sum, order) => sum + (order.NetAmount || 0), 0))}
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg border shadow-sm">
                      <div className="text-sm text-gray-600">Avg. Order</div>
                      <div className="text-xl font-bold">
                        {formatCurrency(orderHistory.reduce((sum, order) => sum + (order.NetAmount || 0), 0) / orderHistory.length)}
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg border shadow-sm">
                      <div className="text-sm text-gray-600">Total Discounts</div>
                      <div className="text-xl font-bold text-green-600">
                        {formatCurrency(orderHistory.reduce((sum, order) => sum + (order.Discount || 0) + (order.VoucherAmount || 0), 0))}
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg border shadow-sm">
                      <div className="text-sm text-gray-600">Completed</div>
                      <div className="text-xl font-bold text-green-600">
                        {orderHistory.filter(order => order.OrderStatusId === 1923).length}
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg border shadow-sm">
                      <div className="text-sm text-gray-600">Pending</div>
                      <div className="text-xl font-bold text-yellow-600">
                        {orderHistory.filter(order => order.OrderStatusId === 1922).length}
                      </div>
                    </div>
                  </div>

                  {/* DataTables Controls */}
                  <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="itemsPerPage" className="text-sm">Show</Label>
                      <select
                        id="itemsPerPage"
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(e.target.value)}
                        className="border rounded px-2 py-1 text-sm"
                      >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                      </select>
                      <span className="text-sm">entries / page</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, orderHistory.length)} of {orderHistory.length} entries
                    </div>
                  </div>

                  {/* Orders List */}
                  <div className="space-y-3">
                    {paginatedOrders.map((order) => (
                      <div key={order.OrderMasterId} className="border rounded-lg overflow-hidden bg-white">
                        {/* Order Header */}
                        <div className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                            <div className="space-y-1">
                              <div className="font-medium">{order.OrderNumber}</div>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Calendar className="h-3 w-3" />
                                {formatDate(order.OrderDate)}
                              </div>
                              <div className="flex items-center gap-2">
                                {getStatusBadge(order.OrderStatusId)}
                                <span className="text-xs text-gray-500">
                                  {order.OrderMode}
                                </span>
                              </div>
                            </div>

                            <div className="space-y-1">
                              <div className="font-medium">{order.CustomerName || "No Name"}</div>
                              <div className="text-sm text-gray-600">{order.PhoneNumber}</div>
                              {order.CompleteAddress && (
                                <div className="text-sm text-gray-600 flex items-start gap-1">
                                  <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                  <span className="text-xs">{order.CompleteAddress}</span>
                                </div>
                              )}
                            </div>

                            <div className="space-y-1">
                              <div className="font-medium">{formatCurrency(order.NetAmount)}</div>
                              <div className="text-sm text-gray-600">
                                Gross: {formatCurrency(order.Gross)}
                              </div>
                              <div className="text-sm text-gray-600">
                                Tax: {formatCurrency(order.Tax)} ({order.GSTPercent}%)
                              </div>
                              {(order.Discount > 0 || order.VoucherAmount > 0) && (
                                <div className="flex items-center gap-1 text-sm text-green-600">
                                  <Tag className="h-3 w-3" />
                                  <span>Discount: {formatCurrency((order.Discount || 0) + (order.VoucherAmount || 0))}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-1 text-sm">
                                {getPaymentModeIcon(order.PaymentMode)}
                                <span className="text-gray-600">{order.PaymentMode}</span>
                              </div>
                            </div>

                            <div className="flex justify-between items-center">
                              <div className="space-y-1 text-sm">
                                <div className="flex items-center gap-2">
                                  <Building className="h-3 w-3" />
                                  <span>{order.BranchName}</span>
                                </div>
                                {order.DeliveryTimeInMinutes && (
                                  <div className="text-xs text-gray-500">
                                    {order.DeliveryTimeInMinutes} mins
                                  </div>
                                )}
                              </div>

                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => toggleOrderExpansion(order.OrderMasterId)}
                                className="flex items-center gap-1"
                                disabled={!order.hasItems}
                              >
                                {expandedOrders.has(order.OrderMasterId) ? (
                                  <>
                                    <Minus className="h-3 w-3" />
                                    <span>Hide Items</span>
                                  </>
                                ) : (
                                  <>
                                    <Plus className="h-3 w-3" />
                                    <span>
                                      {order.hasItems ? `Show Items (${order.items?.length || 0})` : "No Items"}
                                    </span>
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Order Items - Collapsible */}
                        {expandedOrders.has(order.OrderMasterId) && order.hasItems && (
                          <div className="border-t bg-gray-50 p-4">
                            <h4 className="font-medium text-gray-700 mb-3">Ordered Items</h4>
                            {renderOrderItems(order.items, order)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg">
                      <div className="text-sm text-gray-600">
                        Page {currentPage} of {totalPages}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => goToPage(1)}
                          disabled={currentPage === 1}
                        >
                          First
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => goToPage(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>

                        {getPageNumbers().map(page => (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => goToPage(page)}
                          >
                            {page}
                          </Button>
                        ))}

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => goToPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => goToPage(totalPages)}
                          disabled={currentPage === totalPages}
                        >
                          Last
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center p-8 bg-gray-50 rounded-lg">
                  <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No order history found for this phone number.</p>
                  <p className="text-sm text-gray-500 mt-1">Please check the phone number and try again.</p>
                </div>
              )}
            </div>
          )}

          {!searched && (
            <div className="text-center p-8 bg-gray-50 rounded-lg">
              <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Enter a phone number to search for customer order history.</p>
              <p className="text-sm text-gray-500 mt-1">The system will display all orders associated with this phone number.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}